import { FormSection } from '@/api/rest/connectionApplication/useGetConnectionApplicationSchema';
import { LowCarbonTech } from '@payaca/types/deviceTypes';
import { omit } from 'lodash-es';

export const DevicesFieldArrayKeys = ['devicesToInstall', 'existingDevices'];

type DisplayIfConditionBase = {
  property: string;
};
type DisplayIfConditionValue = DisplayIfConditionBase & {
  value: string;
};
type DisplayIfConditionValueIsSet = DisplayIfConditionBase & {
  valueIsSet: boolean;
};
export type DisplayIfCondition =
  | DisplayIfConditionValue
  | DisplayIfConditionValueIsSet;

/**
 * Get list of device types from the devicesToInstall schema
 **/
const getDeviceTypesFromDevicesToInstall = (
  schemaSection: FormSection,
  lcts: LowCarbonTech[]
) => {
  const schemaItems = [];
  if (lcts.includes('HP')) {
    schemaItems.push(
      schemaSection.items.oneOf.find(
        (item: any) => item.title === 'DemandDatabaseToInstallHPV1' // ena db for hp
      )
    );
  }
  if (['EVCP_DC', 'EVCP_AC'].some((d: any) => lcts.includes(d))) {
    schemaItems.push(
      schemaSection.items.oneOf.find(
        (item: any) => item.title === 'DemandDatabaseToInstallGeneralV1' // ena db for non-hp
      )
    );
  }
  if (['BATTERY', 'SOLAR_PV', 'EVV2G'].some((d: any) => lcts.includes(d))) {
    schemaItems.push(
      schemaSection.items.oneOf.find(
        (item: any) => item.title === 'GenerationDeviceToInstallV1' // ena db for generation
      )
    );
  }
  const devicesSet = new Set(
    schemaItems.flatMap((db: any) => {
      // Special case for all  generation devices, which as of ENA API 1.6.0
      // allow any device type in the TTR database to be installed.
      if (
        db.properties.deviceClass.const === 'GEN_DEVICE_GENERAL_ENA_REGISTERED'
      ) {
        return ['BATTERY', 'SOLAR_PV', 'V2G_INVERTER'].filter((d) =>
          lcts.includes(d as LowCarbonTech)
        );
      } else if (db.properties.deviceType.const) {
        return [db.properties.deviceType.const];
      } else if (db.properties.deviceType.enum) {
        return db.properties.deviceType.enum;
      }
      return [];
    })
  );

  if (lcts.includes('EVV2G')) {
    // EVV2G device types require showing V2G_INVERTER and EVCP_AC
    devicesSet.add('V2G_INVERTER');
    devicesSet.add('EVCP_AC');
  }

  return Array.from(devicesSet);
};

export const getDeviceTypesFromSchemaSection = (
  schemaSection: FormSection,
  fieldKey: string,
  lcts?: LowCarbonTech[]
) => {
  switch (fieldKey) {
    case 'devicesToInstall':
      if (lcts) {
        return getDeviceTypesFromDevicesToInstall(schemaSection, lcts);
      }
      return [];
    case 'existingDevices':
      return getDeviceTypesFromExistingDevices(schemaSection);
    default:
      return [];
  }
};

/**
 * Get list of device types from the existingDevices schema
 */
const getDeviceTypesFromExistingDevices = (schemaSection: FormSection) => {
  const schemaItems = schemaSection.items.oneOf.filter((item: any) =>
    [
      'ExistingGenDevicesInDatabaseV1',
      'ExistingDemandDevicesInDatabaseV1',
    ].includes(item.title)
  );
  const devicesSet = schemaItems
    .flatMap((db: any) => db.properties.deviceType.enum)
    .reduce((acc: any, x: any) => {
      if (x) {
        acc.add(x);
      }
      return acc;
    }, new Set());
  return Array.from(devicesSet);
};

/**
 * Fetch the JSON Schema definition for a given device type (e.g. 'EVCP_AC')
 */
export const getDeviceClassSchemaForDeviceType = (
  schemaSection: FormSection,
  deviceType: string
) => {
  const deviceClassSchemaEnumMatch = schemaSection.items.oneOf.find(
    (item: any) => {
      const deviceTypeOptions = getDeviceTypeOptions(item);

      return deviceTypeOptions?.includes(deviceType);
    }
  );
  if (deviceClassSchemaEnumMatch) {
    return deviceClassSchemaEnumMatch;
  }

  const deviceClassSchema = schemaSection.items.oneOf.find((item: any) => {
    const deviceTypeOptions = getDeviceTypeOptions(item);
    // Generation devices have no restriction on deviceType
    return !deviceTypeOptions;
  });
  return deviceClassSchema;
};

/**
 * What device types are valid for this device class's schema?
 * E.g. deviceClass "DEMAND_DEVICE_GENERAL_NOT_REGISTERED" returns ['EVCP_AC', 'EVCP_DC']
 * If null, there was no constraint specified
 */
const getDeviceTypeOptions = (item: any): string[] | null => {
  const deviceTypeField = item.properties.deviceType;
  if (deviceTypeField.enum) return deviceTypeField.enum;
  if (deviceTypeField.const) return [deviceTypeField.const];
  return null;
};

export const getDeviceClassForDeviceType = (
  schemaSection: FormSection,
  deviceType: string
) => {
  const item = getDeviceClassSchemaForDeviceType(schemaSection, deviceType);
  return item.properties.deviceClass.const;
};

export const getSchemaDataForDevice = (
  schemaSection: FormSection,
  definitions: Record<string, any>,
  watchedDeviceType: string
) => {
  // get device class for device type
  const deviceClass =
    watchedDeviceType &&
    getDeviceClassForDeviceType(schemaSection, watchedDeviceType);

  let specificDeviceClassSchema = getSchemaDataForDiscriminatorField(
    schemaSection.items.discriminator,
    definitions,
    deviceClass
  );

  if (!specificDeviceClassSchema) {
    // no mapping specified - use the current objects schema (SOLAR_PV newDevices schema)
    specificDeviceClassSchema = schemaSection.items.oneOf.find(
      (item: any) => item.properties.deviceClass.const === deviceClass
    );
  }

  let schemaData = null;

  if (specificDeviceClassSchema) {
    schemaData = {
      ...specificDeviceClassSchema,
      // omit the deviceType field from the schema as this field is already being shown
      properties: omit(specificDeviceClassSchema.properties, 'deviceType'),
    };

    const propertyKeys = Object.keys(schemaData.properties);
    for (const propertyKey of propertyKeys) {
      if (propertyKey !== 'deviceSysRef') {
        // displayIf property so subsequent fields are only shown after deviceSysRef is set
        schemaData.properties[propertyKey].json_schema_extra.displayIf =
          mergeDisplayIfConditions(
            schemaData.properties[propertyKey].json_schema_extra.displayIf,
            {
              property: 'deviceSysRef',
              valueIsSet: true,
            }
          );
      }
    }
  }
  return schemaData;
};

const mergeDisplayIfConditions = (
  currentDisplayIf: DisplayIfCondition[] | DisplayIfCondition,
  displayIfToAdd: DisplayIfCondition
) => {
  if (!currentDisplayIf) {
    return displayIfToAdd;
  }
  return [
    ...(Array.isArray(currentDisplayIf)
      ? currentDisplayIf
      : [currentDisplayIf]),
    displayIfToAdd,
  ];
};

export const getSchemaDataForDiscriminatorField = (
  discriminatorObj: any, // discriminator object containing property and mapping refs
  definitions: Record<string, any>,
  discriminatorPropertyValue?: any
) => {
  // get the mapping path for the selected value
  const mappingPath =
    discriminatorPropertyValue &&
    discriminatorObj.mapping[discriminatorPropertyValue];

  // get the schema for the selected value
  let discriminatorSchemaData;
  if (mappingPath) {
    discriminatorSchemaData = resolveMappingRef(
      {
        definitions,
      },
      mappingPath
    );
  }
  return discriminatorSchemaData;
};

/**
 * Get the result of a mapping reference within a schema
 * @param mappingPath i.e. #/definitions/ExistingGenDevicesInDatabaseV1
 * @param schema { defintions: { ExistingGenDevicesInDatabaseV1: { ... } } }
 */
const resolveMappingRef = (
  schema: Record<string, any>,
  mappingPath?: string
) => {
  if (!mappingPath) {
    return;
  }
  const parts = mappingPath
    .split('/')
    .filter((f: any) => Boolean(f) && f !== '#'); // Split path and filter out empty strings/# symbol
  let result = schema;
  parts.forEach((part: any) => {
    result = result[part];
  });

  return result;
};
